import { PageRequest } from "@shared/zod/schemas/requests/page"
import {
  AdminCreateUserInvitationRequest,
  CreateUserInvitationRequest,
} from "@shared/zod/schemas/requests/userInvitationRequests"
import { DefaultResponse } from "@shared/zod/schemas/responses/common"
import {
  CreateUserInvitationResponse,
  UserInvitationPageResponse,
  UserInvitationResponse,
} from "@shared/zod/schemas/responses/userInvitations"

import { axiosAuth } from "./generic/AxiosInterceptor/AxiosInterceptor"

/** The invitation endpoints nested under a scope (`/practices/:id/...` or `/careGroups/:id/...`). */
const scopedEndpoints = (scope: "practices" | "careGroups") => ({
  create: async (scopeId: string, req: CreateUserInvitationRequest): Promise<CreateUserInvitationResponse> => {
    const resp = await axiosAuth({ url: `/${scope}/${scopeId}/userinvitations`, method: "post", data: req })
    return resp.data
  },
  list: async (scopeId: string): Promise<UserInvitationResponse[]> => {
    const resp = await axiosAuth({ url: `/${scope}/${scopeId}/userinvitations`, method: "get" })
    return resp.data
  },
  resend: async (scopeId: string, invitationId: string): Promise<CreateUserInvitationResponse> => {
    const resp = await axiosAuth({
      url: `/${scope}/${scopeId}/userinvitations/${invitationId}/resend`,
      method: "post",
    })
    return resp.data
  },
  withdraw: async (scopeId: string, invitationId: string): Promise<DefaultResponse> => {
    const resp = await axiosAuth({ url: `/${scope}/${scopeId}/userinvitations/${invitationId}`, method: "delete" })
    return resp.data
  },
})

// --- Practice scope -------------------------------------------------------

export const {
  create: createForPractice,
  list: listForPractice,
  resend: resendForPractice,
  withdraw: withdrawForPractice,
} = scopedEndpoints("practices")

// --- Care group scope -----------------------------------------------------

export const {
  create: createForCareGroup,
  list: listForCareGroup,
  resend: resendForCareGroup,
  withdraw: withdrawForCareGroup,
} = scopedEndpoints("careGroups")

// --- Admin (cross-practice) ----------------------------------------------

export const adminCreate = async (req: AdminCreateUserInvitationRequest): Promise<CreateUserInvitationResponse> => {
  const resp = await axiosAuth({ url: `/admin/userinvitations`, method: "post", data: req })
  return resp.data
}

export const adminList = async (query?: PageRequest): Promise<UserInvitationPageResponse> => {
  const resp = await axiosAuth({ url: `/admin/userinvitations`, method: "get", params: query })
  return resp.data
}

export const adminResend = async (invitationId: string): Promise<CreateUserInvitationResponse> => {
  const resp = await axiosAuth({ url: `/admin/userinvitations/${invitationId}/resend`, method: "post" })
  return resp.data
}

export const adminWithdraw = async (invitationId: string): Promise<DefaultResponse> => {
  const resp = await axiosAuth({ url: `/admin/userinvitations/${invitationId}`, method: "delete" })
  return resp.data
}

// --- Acceptance (authenticated: connect an existing account) -------------

export const acceptWithExistingAccount = async (token: string): Promise<DefaultResponse> => {
  const resp = await axiosAuth({ url: `/userinvitations/${token}/accept-existing`, method: "post" })
  return resp.data
}
